import { styled } from 'styled-components'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrInput from '../WizrInput'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import { Form, Select } from 'antd'
import WizrText from '../WizrText'
import { useEffect, useState } from 'react'
import WizrDropDown from '../WizrDropDown'
import { useGetReportsListSelector } from '../../redux/selectors/content'
import { dispatchGetColumnValuesForFilter } from '../../redux/actions/content'
import { 
  scheduleIntervalOptions,
  monthOptions,
  dayOfMonthOptions,
  dayOfWeekOptions,
  hourOptions,
  minuteOptions,
  parseCronExpression,
  generateCronExpression
} from '../../utils'
// @ts-ignore
// import Cron from 'react-cron-generator'
// import 'react-cron-generator/dist/cron-builder.css'

const StyledContainer = styled(WizrFlexLayout)`
  padding: 0px 32px 32px 32px;
  width: 100%;
  max-height: 800px;
  overflow: auto;
`

const StyledForm = styled(Form)`
  width: 100%;
  max-width: 480px;
  gap: 10px;
`
const StyledDropDown = styled(WizrDropDown)`
  height: 50px;
  .ant-select-selector {
    padding-top: 10px !important;
    border-radius:16px;
    height: 50px !important;
    background-color: #f2f4f9 !important;
    .ant-select-selection-placeholder {
      margin-top: 10px;
    }
  }
`

const StyledWizrTagInput = styled(Select)`
  /* Add your custom styles here */
  .ant-select-selection-placeholder {
    text-align: left;
  }
  width: 100%;
  .ant-select-selector {
    min-height: 50px;
    background: #f2f4f9 !important;
    border-radius: 16px !important;
    padding-left: 20px !important;
  }
  .ant-select-selection-item {
    background: white !important;
    border-radius: 20px !important;
    border: 1px solid black !important;
    gap: 8px !important;
    padding-right: 8px !important;
  }
  .ant-select-selection-overflow {
    gap: 8px !important;
  }
  overflow: auto; /* Hide any overflow content */
`


const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  background-color: #fff;
`

interface IUploadConfluenceForm {
  onFormSubmission: any
  onCancel: any
  action?: string
  details?: any
}

const NewReportForm = ({ onFormSubmission, onCancel, action = "create", details }: IUploadConfluenceForm): JSX.Element => {

  const [form] = Form.useForm()
  const reportsDetails = useGetReportsListSelector();
  // Convert dashboard charts to dropdown options
  const reportOptions = reportsDetails?.dashboard_info?.charts?.map((chart: any) => ({
    label: chart.chart_name,
    value: chart.chart_id,
  })) || [];

  const [data, setdata] = useState({
    alert_name: details?.name || '',
    email: details?.recipients || '',
    schedule: details?.schedule_details?.cron || '0 0 * * *',
    report: details?.chart_id || ''
  })

  const [scheduleInterval, setScheduleInterval] = useState('year')
  const [scheduleConfig, setScheduleConfig] = useState({
    month: 1,
    dayOfMonth: 1,
    dayOfWeek: 1,
    hour: 1,
    minute: 20
  })
  const [cronExpression, setCronExpression] = useState('* * * * *')

  // Dynamic filters state
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<string[]>([])
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({})
  const [columnOptions_Values, setColumnOptions_Values] = useState<{ [key: string]: any[] }>({})
  const [loadingColumns, setLoadingColumns] = useState<{ [key: string]: boolean }>({})
  
  // State for custom date ranges (for time-based filters)
  const [customDateRanges, setCustomDateRanges] = useState<{
    [key: string]: { start: string | null; end: string | null }
  }>({})

  // Helper function to check if a column is a time-based filter
  const isTimeBasedFilter = (columnName: string): boolean => {
    const lowerName = columnName.toLowerCase()
    return lowerName.includes('time of resolution') || 
           lowerName.includes('created date') || 
           lowerName.includes('updated date') || 
           lowerName.includes('first response time') ||
           lowerName.includes('created_date') ||
           lowerName.includes('updated_date') ||
           lowerName.includes('time_of_resolution') ||
           lowerName.includes('first_response_time')
  }

  // Helper function to create date range for predefined time periods
  const createDateRange = (selectedValue: string): { start: Date; end: Date } | null => {
    const end = new Date()
    const start = new Date()
    
    switch (selectedValue) {
      case '1 Day':
        start.setDate(start.getDate() - 1)
        break
      case '1 Week':
        start.setDate(start.getDate() - 7)
        break
      case '1 Month':
        start.setMonth(start.getMonth() - 1)
        break
      case '1 Quarter':
        start.setMonth(start.getMonth() - 3)
        break
      default:
        return null
    }
    
    return { start, end }
  }

  // Helper function to convert date range to milliseconds
  const convertToMilliseconds = (column: string, selectedValue: string): number[] | null => {
    if (selectedValue === 'Custom' && customDateRanges[column]) {
      const { start, end } = customDateRanges[column]
      if (start && end) {
        return [new Date(start).getTime(), new Date(end).getTime()]
      }
      return null
    }
    
    const dateRange = createDateRange(selectedValue)
    if (dateRange) {
      return [dateRange.start.getTime(), dateRange.end.getTime()]
    }
    return null
  }

  const fetchColumnValues = async (columnName: string) => {
    // Don't fetch if already loading or already have data
    if (loadingColumns[columnName] || columnOptions_Values[columnName]) {
      return
    }

    setLoadingColumns(prev => ({ ...prev, [columnName]: true }))
    
    try {
      // Special case for time-based filters
      if (isTimeBasedFilter(columnName)) {
        const timeOptions = [
          { label: '1 Day', value: '1 Day' },
          { label: '1 Week', value: '1 Week' },
          { label: '1 Month', value: '1 Month' },
          { label: '1 Quarter', value: '1 Quarter' },
          { label: 'Custom', value: 'Custom' }
        ]
        
        setColumnOptions_Values(prev => ({
          ...prev,
          [columnName]: timeOptions
        }))
        setLoadingColumns(prev => ({ ...prev, [columnName]: false }))
        return
      }

      const payload = {
        column_name: columnName,
      }
      const response = await dispatchGetColumnValuesForFilter(payload)
      
      // Assuming the response contains an array of values
      // Update this based on the actual API response structure
      const values = response?.data?.values
      const formattedOptions = Array.isArray(values) 
        ? values.map((value: any) => ({
            label: typeof value === 'object' ? value.label || value.name || value.value : value,
            value: typeof value === 'object' ? value.value || value.name || value : value
          }))
        : []
      
      setColumnOptions_Values(prev => ({
        ...prev,
        [columnName]: formattedOptions
      }))
    } catch (error) {
      console.error('Error fetching column values for', columnName, ':', error)
      setColumnOptions_Values(prev => ({
        ...prev,
        [columnName]: []
      }))
    } finally {
      setLoadingColumns(prev => ({ ...prev, [columnName]: false }))
    }
  }

  // Generate filters payload for form submission
  const generateFiltersPayload = () => {
    const conditions = selectedFilterColumns
      .filter(column => filterValues[column] && filterValues[column] !== '')
      .map(column => {
        const isTimeBased = isTimeBasedFilter(column)
        let values = Array.isArray(filterValues[column]) ? filterValues[column] : [filterValues[column]]
        
        // Handle time-based filters
        if (isTimeBased && values.length > 0) {
          const selectedValue = values[0] // Time filters typically have single selection
          const milliseconds = convertToMilliseconds(column, selectedValue)
          
          if (milliseconds) {
            return {
              type: column,
              conditionBy: '<>', // Use '<>' for time range filters
              values: milliseconds
            }
          }
          return null // Skip if no valid date range
        }
        
        // Handle regular filters
        return {
          type: column,
          conditionBy: '=', // Use "=" for regular filters
          values: values
        }
      })
      .filter(condition => condition !== null) // Remove null conditions
    
    // Return the conditions wrapped in the required array structure
    return conditions.length > 0 ? [{ conditions }] : []
  }


  // Update cron expression when schedule config or interval changes
  useEffect(() => {
    const newCron = generateCronExpression(scheduleInterval, scheduleConfig);
    setCronExpression(newCron);
    setdata({ ...data, schedule: newCron });
  }, [scheduleInterval, scheduleConfig])

  // Update cron expression when schedule changes
  useEffect(() => {
    setdata({ ...data, schedule: cronExpression })
  }, [cronExpression])

  // Set form values when details change (for edit mode)
  useEffect(() => {
    if (details && action === 'edit') {
      const cronValue = details.schedule_details?.cron || '* * * * *'
      const parsed = parseCronExpression(cronValue);
      
      if (parsed) {
        setScheduleInterval(parsed.interval);
        setScheduleConfig({
          ...scheduleConfig,
          ...parsed.config
        });
      }
      
      setdata({
        alert_name: details.name || '',
        email: details.recipients || '',
        schedule: cronValue,
        report: details.chart_id || ''
      });
      setCronExpression(cronValue)
      form.setFieldsValue({
        alert_name: details.name || '',
        email: details.recipients || '',
        report: details.chart_id || ''
      });

      // Handle filters for edit mode
      if (details.filters && Array.isArray(details.filters) && details.filters.length > 0) {
        // Handle filter format
        const filterConditions = details.filters[0]?.conditions || []
        const filterColumns = filterConditions.map((condition: any) => condition.type)
        const filterVals: { [key: string]: any } = {}
        
        filterConditions.forEach((condition: any) => {
          if (isTimeBasedFilter(condition.type) && condition.conditionBy === '<>') {
            // For time-based filters, convert milliseconds back to readable format
            const [startMillis, endMillis] = condition.values
            const diffMillis = endMillis - startMillis
            const diffDays = Math.round(diffMillis / (1000 * 60 * 60 * 24))
            
            if (diffDays <= 1) {
              filterVals[condition.type] = ['1 Day']
            } else if (diffDays <= 7) {
              filterVals[condition.type] = ['1 Week']
            } else if (diffDays <= 31) {
              filterVals[condition.type] = ['1 Month']
            } else if (diffDays <= 93) {
              filterVals[condition.type] = ['1 Quarter']
            } else {
              filterVals[condition.type] = ['Custom']
              // Set custom date range
              setCustomDateRanges(prev => ({
                ...prev,
                [condition.type]: {
                  start: new Date(startMillis).toISOString().split('T')[0],
                  end: new Date(endMillis).toISOString().split('T')[0]
                }
              }))
            }
          } else {
            // For regular filters
            filterVals[condition.type] = condition.values
          }
        })
        
        setSelectedFilterColumns(filterColumns)
        setFilterValues(filterVals)
        
        // Fetch column values for all filter columns to ensure dropdown options are populated
        filterColumns.forEach(async (columnName: string) => {
          await fetchColumnValues(columnName)
        })
      }
    } else {
      // Set default values for new report
      const defaultCron = '* * * * *'; // Default: Daily at 9:00 AM
      const parsed = parseCronExpression(defaultCron);
      
      if (parsed) {
        setScheduleInterval(parsed.interval);
        setScheduleConfig({
          ...scheduleConfig,
          ...parsed.config
        });
      }
      setCronExpression(defaultCron);
    }
  }, [details, action, form]);





  const handleSave = () => {
    const payload: any = {
      name: data.alert_name,
      recipients: data.email,
      chart_id: data.report,
      schedule_details: {
        cron: cronExpression
      },
      filters: generateFiltersPayload(),
      enabled: true
    };

    if (action === 'edit' && details?.report_id) {
      payload.report_id = details.report_id;
    }
    onFormSubmission(payload);
  }

  return (
    <StyledContainer background='#FFF'>
      <StyledForm
        name='confluence'
        form={form}
        layout='vertical'
        onFinish={onFormSubmission}
        onFinishFailed={() => null}
        autoComplete='off'
      >
        <Form.Item
          name='alert_name'
        //   rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Report Name</WizrText>
            <WizrInput
              required={false}
              value={data?.alert_name}
              placeholder='Enter Report Name'
              onChange={(value: string) => {
                setdata({ ...data, alert_name: value });
                form.setFieldsValue({ alert_name: value });
              }}
              style={{
                background: '#F2F4F9',
                borderRadius: '16px',
                height: '50px'
              }}
            />
          </InputContainer>
        </Form.Item>

        <Form.Item
          name='email'
        //   rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Email</WizrText>
            <WizrInput
              required={false}
              value={data?.email}
              placeholder='Enter Email'
              onChange={(value: string) => {
                setdata({ ...data, email: value });
                form.setFieldsValue({ email: value });
              }}
              style={{
                background: '#F2F4F9',
                borderRadius: '16px',
                height: '50px'
              }}
            />
          </InputContainer>
        </Form.Item>

        <Form.Item>
          <InputContainer>
            <WizrText type='body2'>Schedule Interval (Every)</WizrText>
            <StyledDropDown
              value={scheduleInterval}
              options={scheduleIntervalOptions}
              onChange={(value: any) => {
                setScheduleInterval(value);
              }}
            />
          </InputContainer>
        </Form.Item>

        {scheduleInterval === 'year' && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>Month of Year</WizrText>
              <StyledDropDown
                value={scheduleConfig.month}
                options={monthOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, month: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>Day of Month</WizrText>
              <StyledDropDown
                value={scheduleConfig.dayOfMonth}
                options={dayOfMonthOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, dayOfMonth: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {scheduleInterval === 'week' && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>On Day</WizrText>
              <StyledDropDown
                value={scheduleConfig.dayOfWeek}
                options={dayOfWeekOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, dayOfWeek: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month' || scheduleInterval === 'week' || scheduleInterval === 'day') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>At Hour</WizrText>
              <StyledDropDown
                value={scheduleConfig.hour}
                options={hourOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, hour: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month' || scheduleInterval === 'week' || scheduleInterval === 'day' || scheduleInterval === 'hour') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>At Minute</WizrText>
              <StyledDropDown
                value={scheduleConfig.minute}
                options={minuteOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, minute: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        <Form.Item
          name='report'
        //   rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Select Report</WizrText>
            <StyledDropDown
              value={data?.report}
              options={reportOptions}
              onChange={(value: any, option: any) => {
                setdata({ ...data, report: value });
                form.setFieldsValue({ report: value });
              }}
            />
          </InputContainer>
        </Form.Item>

        <Form.Item style={{ marginTop: '32px' }}>
          <WizrFlexSpacing
            justifyContent='flex-end'
            flexDirection='row'
            //gap='16px'
            background='#FFF'
          >
            <WizrButton
              type='default'
              text='Cancel'
              onClick={onCancel}
              style={{
                borderRadius: '20px',
                padding: '8px 24px',
                border: '1px solid #E5E5E5',
                height: '40px',
                minWidth: '120px'
              }}
            />
            <WizrButton
              text='Save'
              type='primary'
              onClick={handleSave}
              style={{
                borderRadius: '20px',
                padding: '8px 24px',
                background: '#000',
                height: '40px',
              }}
            />
          </WizrFlexSpacing>
        </Form.Item>
      </StyledForm>
    </StyledContainer>
  )
}

export default NewReportForm